import dayjs from 'dayjs';
import { useContext, useEffect } from 'react';
import { useState, useRef } from 'react';
import { store } from 'components/Store';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { CalendarPopup } from 'components/Dashboard/Filters/ui/DateFilter';
import {
  createIncludeRepostsOption,
  createTweetsDateIntervalValue,
  DateOptionsMapping,
} from 'components/Dashboard/Analytics/createAnalyticsState';
import { useLocation } from 'react-router';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import { Portal } from 'react-portal';

const ChartViews = ['Day', 'Week', 'Month'];
const ChartTypes = ['Line', 'Bar'];
const MapTypes = ['Heat map', 'Bubble chart map'];
const DateOptions = DateOptionsMapping;

const FiltersBar = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const location = useLocation();

  const analyticsState = props?.analyticsState ?? state?.analyticsState;

  const { typeOfChartSections, scrollBarPosition, chartName } = props;
  const sentimentChanges = location.pathname.includes('/sentiment-changes') || chartName === 'Sentiment changes';
  const interestByRegion = location.pathname.includes('/interest-by-region') || chartName === 'Interest by region';

  const hideDateFilter = interestByRegion || props?.hideDateFilter;
  const hideChartView = interestByRegion;

  const modifyFiltersBarFunction = ({ property, newValue }) => {
    let newState = property === 'CompleteState' ? newValue : { ...analyticsState, [property]: newValue };
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'analyticsState',
      value: newState,
    });
  };

  const modifyFiltersBar = props?.modifyFiltersBar ?? modifyFiltersBarFunction;
  return (
    <div className='flex-grow-1 d-flex justify-content-end analytics-filters-bar-container'>
      {!hideDateFilter && (
        <DateFilterButton
          analyticsState={analyticsState}
          modifyFiltersBar={modifyFiltersBar}
          customLeftValue={0}
          scrollBarPosition={scrollBarPosition}
        />
      )}

      {typeOfChartSections && (
        <TypeOfChartOptions
          sentimentChanges={sentimentChanges}
          interestByRegion={interestByRegion}
          modifyFiltersBar={modifyFiltersBar}
          analyticsState={analyticsState}
        />
      )}

      {!hideChartView && <ChartViewOptions analyticsState={analyticsState} modifyFiltersBar={modifyFiltersBar} />}
    </div>
  );
};

const TypeOfChartOptions = (props) => {
  const { sentimentChanges, analyticsState, modifyFiltersBar, interestByRegion } = props;
  const { typeOfChartSentimentChanges, typeOfChart, typeOfChartInterestByRegion } = analyticsState;
  const types = interestByRegion ? MapTypes : ChartTypes;

  return (
    <div className='filter-group ml-3 nowrap-item chart-type-filter'>
      {types.map((item) => {
        const propertyToCompare = sentimentChanges
          ? typeOfChartSentimentChanges
          : interestByRegion
            ? typeOfChartInterestByRegion
            : typeOfChart;
        return (
          <button
            key={`type-${item}`}
            className={`general-button filter-item-button ${propertyToCompare === item ? 'active' : ''}`}
            onClick={() => {
              if (interestByRegion) {
                modifyFiltersBar({
                  property: 'typeOfChartInterestByRegion',
                  newValue: item,
                });
              } else {
                modifyFiltersBar({ property: 'typeOfChart', newValue: item });
                if (sentimentChanges) {
                  modifyFiltersBar({
                    property: 'typeOfChartSentimentChanges',
                    newValue: item,
                  });
                }
              }
            }}
          >
            {item}
          </button>
        );
      })}
    </div>
  );
};

const ChartViewOptions = (props) => {
  const { analyticsState, modifyFiltersBar } = props;
  const { currentView } = analyticsState;
  return (
    <div className='filter-group ml-3 nowrap-item '>
      {ChartViews.map((item) => (
        <button
          key={`type-${item}`}
          className={`general-button filter-item-button ${currentView === item ? 'active' : ''}`}
          onClick={() => {
            modifyFiltersBar({ property: 'currentView', newValue: item });
          }}
        >
          {item}
        </button>
      ))}
    </div>
  );
};

const DateFilterButton = (props) => {
  const {
    analyticsState,
    modifyFiltersBar,
    customTopValue = 42,
    customLeftValue = 40,
    scrollBarPosition,
    customOptionsNames,
    activeDateIntervalProp = 'activeDateInterval',
    titleModifier,
    maxHeightValue,
    chartType,
  } = props;
  const activeDateInterval = analyticsState[activeDateIntervalProp];
  const [openOptions, setOpenOptions] = useState(false);
  const [autoHeightMaxValue, setAutoHeightMaxValue] = useState(maxHeightValue);
  const [dropdownReady, setDropdownReady] = useState(false);
  const [calendarState, setCalendarState] = useState({
    active: false,
    filterProperty: '',
    selectedDay: new Date(),
    name: '',
    range:
      activeDateInterval.filter.length === 2 && activeDateInterval.name === 'Custom'
        ? {
            from: new Date(activeDateInterval.filter[0]),
            to: new Date(activeDateInterval.filter[1]),
          }
        : {
            from: '',
            to: '',
          },
  });

  const filtersElement = useRef(null);
  const location = useLocation();
  const hideFilters = () => {
    setOpenOptions(false);
  };
  const { windowHeight } = useWindowHeight();

  useEffect(() => {
    if (!!scrollBarPosition) {
      setOpenOptions(false);
    }
  }, [scrollBarPosition]);

  let createStyles = () => {
    if (filtersElement.current) {
      let { top, width, left } = filtersElement.current.getBoundingClientRect();
      return {
        position: 'fixed',
        top: top + customTopValue,
        right: 0,
        left: activeDateInterval.name === 'Custom' ? left : left - customLeftValue,
        width,
      };
    }
  };

  const applyCalendarFilter = () => {
    let daysDiff = dayjs(calendarState.range.to).diff(dayjs(calendarState.range.from), 'days');
    const newValue = {
      name: 'Custom',
      filter: [
        dayjs(calendarState.range.from).format('YYYY-MM-DD'),
        dayjs(calendarState.range.to).format('YYYY-MM-DD'),
      ],
    };
    let newAnalyticsState = {
      ...analyticsState,
      [activeDateIntervalProp]: newValue,
    };

    // Only update the tweets reposts toggle when the Tweets date interval is being changed.
    if (activeDateIntervalProp === 'tweetsDateInterval' || chartType === 'Tweets') {
      newAnalyticsState.includeXRepostsTweetsSection = createIncludeRepostsOption({
        tweetsDateInterval: newValue,
      });
    }
    if (daysDiff < 14) {
      newAnalyticsState.currentView = 'Day';
    } else if (daysDiff > 14 && daysDiff <= 90) {
      newAnalyticsState.currentView = 'Week';
    } else {
      newAnalyticsState.currentView = 'Month';
    }
    modifyFiltersBar({ property: 'CompleteState', newValue: newAnalyticsState });
    hideFilters();
  };

  const getTitle = () => {
    if (activeDateInterval.name === 'Custom') {
      let equalYear =
        dayjs(activeDateInterval.filter[0]).get('year') === dayjs(activeDateInterval.filter[1]).get('year');
      let equalMonth =
        dayjs(activeDateInterval.filter[0]).get('month') === dayjs(activeDateInterval.filter[1]).get('month');
      let sameYear = dayjs(activeDateInterval.filter[1]).get('year') === dayjs(new Date()).get('year');
      return `${
        activeDateInterval.filter[0]
          ? dayjs(activeDateInterval.filter[0]).format(equalYear ? (equalMonth ? 'D' : 'D MMMM') : 'D MMMM YYYY')
          : '-'
      } to ${
        activeDateInterval.filter[1]
          ? dayjs(activeDateInterval.filter[1]).format(equalYear ? `D MMMM${!sameYear ? ' YYYY' : ''}` : 'D MMMM YYYY')
          : '-'
      }`;
    } else {
      return titleModifier ? titleModifier(activeDateInterval.name) : activeDateInterval.name;
    }
  };

  const titleRef = useRef(null);
  const optionsContainer = useRef(null);

  useEffect(() => {
    if (optionsContainer?.current) {
      const remainingSpace = windowHeight - optionsContainer?.current?.getBoundingClientRect()?.top;
      const valueToSubstract = remainingSpace > 250 ? 87 : 20;
      setAutoHeightMaxValue(
        remainingSpace > 200 ? `${maxHeightValue ?? remainingSpace - valueToSubstract}px` : `${remainingSpace}px`
      );
      setDropdownReady(true);
    } else {
      setDropdownReady(false);
    }
  }, [openOptions]);

  return (
    <>
      <div className='position-relative graph-date-button-container' ref={filtersElement}>
        <div className='filter-group nowrap-item'>
          <button
            className={`general-button filter-item-button`}
            onClick={() => {
              setOpenOptions(!openOptions);
            }}
          >
            <span
              className={`dropdown-item-element dropdown-item-date-element dropdown-item-element-9 ${
                openOptions ? 'open' : ''
              }`}
              ref={titleRef}
              data-tooltip-content={
                !titleModifier && titleRef?.current?.scrollWidth > 150 && !location.pathname?.includes('/reports')
                  ? getTitle()
                  : undefined
              }
              data-tooltip-id={'general-tooltip'}
              style={{ maxWidth: titleModifier ? 'unset' : undefined }}
            >
              {getTitle()}
            </span>
          </button>
        </div>
        {openOptions && (
          <Portal>
            <div className='general-close-background adjustment-of-position' onClick={hideFilters} />
            <div
              ref={optionsContainer}
              className='login-navbar adjustment-of-position'
              style={{ ...createStyles(), visibility: dropdownReady ? 'visible' : 'hidden' }}
            >
              <CustomScrollbar
                className='simple-scrollbar'
                autoHeight
                autoHeightMin='100%'
                autoHeightMax={autoHeightMaxValue}
              >
                <div className='py-2'>
                  {DateOptions?.filter((item) => !item?.hidden)
                    ?.filter((item) => (customOptionsNames ? customOptionsNames?.includes(item.name) : true))
                    ?.map((item, index) => {
                      const { name } = item;
                      return (
                        <button
                          className='settings-button setting-item general-button d-block text-left w-100 px-4 position-relative'
                          key={`date-${normalize(name)}${index}`}
                          onClick={() => {
                            if (name === 'Custom') {
                              if (window.location.pathname === '/sentiment') {
                                modifyFiltersBar();
                              } else {
                                hideFilters();
                                setCalendarState({
                                  ...calendarState,
                                  active: true,
                                  filterProperty: 'range',
                                  name: name,
                                });
                              }
                            } else {
                              let newAnalyticsState = {
                                ...analyticsState,
                                [activeDateIntervalProp]: item,
                                currentView: name === 'Last 7 days' || name === 'Last 30 days' ? 'Day' : 'Month',
                              };
                              modifyFiltersBar({ property: 'CompleteState', newValue: newAnalyticsState });
                              hideFilters();
                              setCalendarState({
                                ...calendarState,
                                range: { from: '', to: '' },
                              });
                            }
                          }}
                        >
                          {name}
                          {activeDateInterval?.name === name && (
                            <span className='icon-tick topic-selected-mark paragraph-p4' />
                          )}
                        </button>
                      );
                    })}
                </div>
              </CustomScrollbar>
            </div>
          </Portal>
        )}
      </div>
      <CalendarPopup
        showPopup={calendarState.active}
        calendarState={{
          ...calendarState,
          range: {
            from: calendarState.range.from,
            to: calendarState.range.to,
          },
        }}
        setCalendarState={setCalendarState}
        ApplyCalendarFilters={applyCalendarFilter}
        analyticsPage
        setShowPopup={(val) => setCalendarState({ ...calendarState, active: val })}
        resetOnApply={() => {
          const newInterval =
            chartType === 'Tweets'
              ? createTweetsDateIntervalValue()
              : DateOptions.find((item) => item.name === 'Last 12 months');

          let newAnalyticsState = {
            ...analyticsState,
            [activeDateIntervalProp]: newInterval,

            currentView: 'Month',
          };

          if (chartType === 'Tweets') {
            newAnalyticsState = {
              ...newAnalyticsState,
              includeXRepostsTweetsSection: createIncludeRepostsOption({
                tweetsDateInterval: newInterval,
              }),
            };
          }

          modifyFiltersBar({ property: 'CompleteState', newValue: newAnalyticsState });
          hideFilters();
        }}
        disabledRule={
          chartType === 'Tweets' && calendarState.filterProperty === 'range'
            ? (day) => {
                const firstTweetDate = import.meta.env.VITE_APP_FIRST_TWEET_DATE;
                const date = dayjs(day);
                const isAfterToday = date.isAfter(dayjs(), 'day');
                const isBeforeFirstTweet = firstTweetDate ? date.isBefore(dayjs(firstTweetDate), 'day') : false;
                return isBeforeFirstTweet || isAfterToday;
              }
            : undefined
        }
      />
    </>
  );
};

export { DateOptions, ChartViewOptions, TypeOfChartOptions, DateFilterButton };
export default FiltersBar;
